import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { Box, Stack, Avatar, Typography, alpha, Tooltip } from '@mui/material'
import { Construction, LockClock } from '@mui/icons-material'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { MODE_ICONS } from '../utils/constants'
import { GetDateNumeric } from '../../../../../generic/DateTimeFunctions'

/**
 * Device header component
 * Displays the device icon, name, and operation mode
 */
const DeviceHeader = memo(({ deviceName, mode, colors, deviceForecast = null }) => {
  const { t_ } = useSmartTranslation()
  const modeIcon = MODE_ICONS[mode] || MODE_ICONS.MANUAL

  return (
    <Box sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      {/* Left Side: Icon and Name. Takes all the width the mode icons leave free instead of a fixed
          cap, so the name only gets ellipsed once it really does not fit the widget. The
          `minWidth: 0` on both the Stack and the Typography is what lets a flex item shrink below its
          content width — without it the text would overflow instead of ellipsing. */}
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
        <Avatar
          variant='rounded'
          sx={{
            bgcolor: alpha(colors.textLight, 0.1),
            color: colors.textLight,
            width: 32,
            height: 32,
            flexShrink: 0
          }}
        >
          <Construction fontSize='small' />
        </Avatar>
        <Typography
          variant='h6'
          sx={{
            fontWeight: 700,
            letterSpacing: 0.5,
            color: colors.textLight,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: '1 1 auto',
            minWidth: 0
          }}
          title={deviceName}
        >
          {deviceName || 'FAB'}
        </Typography>
      </Stack>

      {/* Right Side: Mode Icon — never shrinks, so it always keeps its space next to the name. */}
      <Stack direction='row' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
        {deviceForecast != null && deviceForecast?.forecast && (
          <Tooltip title={t_('Asset is forecast until') + ' ' + GetDateNumeric(deviceForecast?.forecastDate)}>
            <LockClock style={{ width: '2rem' }} />
          </Tooltip>
        )}
        <img
          src={modeIcon}
          alt={mode || t_('Unknown Mode')}
          style={{ width: '2rem', opacity: 1, aspectRatio: 'auto' }}
        />
      </Stack>
    </Box>
  )
})

DeviceHeader.displayName = 'DeviceHeader'

DeviceHeader.propTypes = {
  deviceName: PropTypes.string,
  mode: PropTypes.string,
  colors: PropTypes.shape({
    textLight: PropTypes.string.isRequired
  }).isRequired,
  deviceForecast: PropTypes.shape({
    forecast: PropTypes.bool,
    forecastDate: PropTypes.date
  })
}

export default DeviceHeader
